/**
 * 视图注册器 - 轻量级单例实现
 * 职责：
 * 1. 注册不同的视图组件
 * 2. 根据规则匹配合适的视图
 * 3. 返回视图组件和 props
 */
import type { RightsContext } from './types';

// Vue 组件类型定义
export type VueComponent = Record<string, unknown> | (() => Promise<unknown>);

// 视图配置接口
export interface ViewConfig {
  // 视图类型标识
  viewType: string;
  // 视图名称
  viewName: string;
  // Vue 组件
  component: VueComponent;
  // 匹配规则（返回 true 表示匹配成功）
  match: (context: RightsContext) => boolean;
  // 优先级（数字越大优先级越高）
  priority: number;
  // 组件 props 生成器
  props?: (context: RightsContext) => Record<string, unknown>;
  // 描述
  description?: string;
}

// 视图匹配结果
export interface ViewMatchResult {
  // 匹配的视图配置
  config: ViewConfig;
  // 组件 props
  props: Record<string, unknown>;
  // 视图类型
  viewType: string;
  // 是否匹配成功
  matched: boolean;
}

// 视图注册器类
class ViewRegistry {
  // 存储所有注册的视图
  private views: ViewConfig[] = [];

  /**
   * 注册视图
   */
  public register(config: ViewConfig): void {
    // 检查是否已存在相同 viewType
    const existingIndex = this.views.findIndex((v) => v?.viewType === config?.viewType);

    if (existingIndex !== -1) {
      // 替换已存在的视图
      this.views[existingIndex] = config;
    } else {
      // 添加新视图
      this.views.push(config);
    }

    // 按优先级排序（降序）
    this.views.sort((a, b) => b.priority - a.priority);
  }

  /**
   * 批量注册视图
   */
  public registerBatch(configs: ViewConfig[]): void {
    configs.forEach((config) => this.register(config));
  }

  /**
   * 根据上下文匹配视图
   */
  public match(context: RightsContext): ViewMatchResult {
    // 遍历所有视图，找到第一个匹配的
    const matchedConfig = this.views.find((config) => {
      try {
        return config.match(context);
      } catch (error) {
        // 匹配失败时静默跳过该视图
        return false;
      }
    });

    // 找到匹配的视图
    if (matchedConfig) {
      return {
        config: matchedConfig,
        props: matchedConfig.props ? matchedConfig.props(context) : {},
        viewType: matchedConfig.viewType,
        matched: true,
      };
    }

    // 没有匹配的视图，返回空结果
    return {
      config: {} as ViewConfig,
      props: {},
      viewType: 'none',
      matched: false,
    };
  }

  /**
   * 获取所有已注册的视图类型
   */
  public getAllViewTypes(): string[] {
    return this.views.map((v) => v.viewType);
  }

  /**
   * 清空所有注册的视图
   */
  public clear(): void {
    this.views = [];
  }

  /**
   * 获取视图数量
   */
  public getViewCount(): number {
    return this.views.length;
  }
}

// 导出单例
export const viewRegistry = new ViewRegistry();

// 开发环境暴露到 window 方便调试
if (process.env.NODE_ENV === 'development') {
  (window as unknown as Record<string, unknown>).__viewRegistry = viewRegistry;
}
